import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';


const ECHOTEXTURE_OPTIONS = [
  'Normal',
  'Not Well Visualized',
  'Hypoechoic',
  'Hyperechoic',
  'Atrophy'
];

const HYDRONEPHROSIS_OPTIONS = [
  'None',
  'Pelviectasis',
  'Mild Hydro',
  'Moderate Hydro',
  'Severe Hydro'
];

const KIDNEY_FINDINGS = [
  'Simple Cyst(s)',
  'Stones',
  'Column Bertin',
  'Stent'
];

const BLADDER_FINDINGS = [
  'Foley Catheter',
  'Mass',
  'Debris',
  'Enlarged Prostate'
];

const MASS_ECHOTEXTURE = [
  'Complex Cystic',
  'Solid Hypoechoic',
  'Solid Hyperechoic'
];

const WAVEFORM_OPTIONS = [
  'Triphasic/Biphasic',
  'Monophasic',
  'High Velocity with Spectral Broadening',
  'Absent'
];

const RENAL_VEIN_OPTIONS = [
  'Patent',
  'No Detectable Flow'
];

const INITIAL_RENAL_ARTERY_DOPPLER_DATA = {
  examType: 'Renal Artery Doppler',
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: '',
  callReport: false,
  phoneNumber: '',
  history: '',
  comments: '',
  aorta: {
    diameter: '',
    peakSystolicVelocity: '',
    waveform: 'Triphasic/Biphasic'
  },
  rightKidney: {
    evaluated: true,
    nephrectomy: false,
    echotexture: 'Normal',
    size1: '',
    size2: '',
    size3: '',
    hydronephrosis: 'None',
    other: [],
    masses: [],
    peakSystolicVelocity: '',
    waveform: 'Triphasic/Biphasic',
    aliasing: false,
    tardusParvus: false,
    highestRI: '',
    lowestRI: '',
    renalToAortaRatio: '',
    estimatedStenosis: '',
    renalVein: 'Patent'
  },
  leftKidney: {
    evaluated: true,
    nephrectomy: false,
    echotexture: 'Normal',
    size1: '',
    size2: '',
    size3: '',
    hydronephrosis: 'None',
    other: [],
    masses: [],
    peakSystolicVelocity: '',
    waveform: 'Triphasic/Biphasic',
    aliasing: false,
    tardusParvus: false,
    highestRI: '',
    lowestRI: '',
    renalToAortaRatio: '',
    estimatedStenosis: '',
    renalVein: 'Patent'
  },
  bladder: {
    nwv: false,
    other: [],
    rightJet: true,
    leftJet: true,
    prevoid: '',
    postvoid: '',
    noVoid: false
  }
};

function RenalArteryDopplerForm({ token, onBack, facilityInfo }) {
  // Tab state: 0=Right Kidney, 1=Left Kidney
  const [activeTab, setActiveTab] = useState(0);
  // Fetch dynamic technologists list
  const { technologists, defaultTech } = useTechnologists();

  const {
    formData,
    updateField,
    updateNestedField,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    resetForm,
    isLoading,
    error,
    success,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
    { ...INITIAL_RENAL_ARTERY_DOPPLER_DATA, technologist: defaultTech },
    'Renal Artery Doppler',
    { token, facilityInfo }
  );

  useEffect(() => {
    if (defaultTech && !formData.technologist) {
      updateField('technologist', defaultTech);
    }
  }, [defaultTech, formData.technologist, updateField]);

  const calculateVolume = (x, y, z) => {
    if (!x || !y || !z) return '';
    const volume = (parseFloat(x) * parseFloat(y) * parseFloat(z) * 0.52).toFixed(1);
    return volume;
  };

  const calculateRenalToAortaRatio = (renalPSV, aortaPSV) => {
    if (!renalPSV || !aortaPSV || parseFloat(aortaPSV) === 0) return '';
    const ratio = (parseFloat(renalPSV) / parseFloat(aortaPSV)).toFixed(2);
    return ratio;
  };

  const calculateEstimatedStenosis = (psv, rar) => {
    if (!psv || !rar) return '';

    const psvValue = parseFloat(psv);
    const rarValue = parseFloat(rar);

    // Normal: PSV < 180 cm/sec and RAR < 3.5
    if (psvValue < 180 && rarValue < 3.5) {
      return 'Normal';
    }
    // Less than 60% Stenosis: PSV > 180 cm/sec and RAR < 3.5
    else if (psvValue > 180 && rarValue < 3.5) {
      return 'Less than 60% Stenosis';
    }
    // Stenosis 60% or greater: PSV > 180 cm/sec and RAR >= 3.5
    else if (psvValue > 180 && rarValue >= 3.5) {
      return 'Stenosis 60% or greater';
    }

    return '';
  };

  // Auto-calculate ratios when PSV values change
  useEffect(() => {
    const rightRatio = calculateRenalToAortaRatio(
      formData.rightKidney.peakSystolicVelocity,
      formData.aorta.peakSystolicVelocity
    );
    if (rightRatio && rightRatio !== formData.rightKidney.renalToAortaRatio) {
      updateNestedField('rightKidney', 'renalToAortaRatio', rightRatio);
    }
  }, [formData.rightKidney.peakSystolicVelocity, formData.aorta.peakSystolicVelocity]);

  useEffect(() => {
    const leftRatio = calculateRenalToAortaRatio(
      formData.leftKidney.peakSystolicVelocity,
      formData.aorta.peakSystolicVelocity
    );
    if (leftRatio && leftRatio !== formData.leftKidney.renalToAortaRatio) {
      updateNestedField('leftKidney', 'renalToAortaRatio', leftRatio);
    }
  }, [formData.leftKidney.peakSystolicVelocity, formData.aorta.peakSystolicVelocity]);

  // Auto-calculate estimated stenosis for right kidney
  useEffect(() => {
    const stenosis = calculateEstimatedStenosis(
      formData.rightKidney.peakSystolicVelocity,
      formData.rightKidney.renalToAortaRatio
    );
    if (stenosis !== formData.rightKidney.estimatedStenosis) {
      updateNestedField('rightKidney', 'estimatedStenosis', stenosis);
    }
  }, [formData.rightKidney.peakSystolicVelocity, formData.rightKidney.renalToAortaRatio]);

  // Auto-calculate estimated stenosis for left kidney
  useEffect(() => {
    const stenosis = calculateEstimatedStenosis(
      formData.leftKidney.peakSystolicVelocity,
      formData.leftKidney.renalToAortaRatio
    );
    if (stenosis !== formData.leftKidney.estimatedStenosis) {
      updateNestedField('leftKidney', 'estimatedStenosis', stenosis);
    }
  }, [formData.leftKidney.peakSystolicVelocity, formData.leftKidney.renalToAortaRatio]);

  const handleKidneyChange = useCallback((side, field, value) => {
    updateNestedField(side, field, value);
  }, [updateNestedField]);

  const handleKidneyOtherChange = useCallback((side, finding) => {
    const currentOther = formData[side].other;
    const newOther = currentOther.includes(finding)
      ? currentOther.filter(f => f !== finding)
      : [...currentOther, finding];

    handleKidneyChange(side, 'other', newOther);
  }, [formData, handleKidneyChange]);

  const addKidneyMass = useCallback((side) => {
    const newMass = {
      location: side === 'rightKidney' ? 'Right Kidney' : 'Left Kidney',
      size1: '',
      size2: '',
      size3: '',
      echotexture: 'Complex Cystic'
    };

    updateNestedField(side, 'masses', [...formData[side].masses, newMass]);
  }, [formData, updateNestedField]);

  const removeKidneyMass = useCallback((side, index) => {
    updateNestedField(side, 'masses', formData[side].masses.filter((_, i) => i !== index));
  }, [formData, updateNestedField]);

  const handleMassChange = useCallback((side, index, field, value) => {
    const updatedMasses = formData[side].masses.map((mass, i) =>
      i === index ? { ...mass, [field]: value } : mass
    );
    updateNestedField(side, 'masses', updatedMasses);
  }, [formData, updateNestedField]);

  const handleBladderOtherChange = useCallback((finding) => {
    const currentOther = formData.bladder.other;
    const newOther = currentOther.includes(finding)
      ? currentOther.filter(f => f !== finding)
      : [...currentOther, finding];

    updateNestedField('bladder', 'other', newOther);
  }, [formData, updateNestedField]);

  const rightKidneyDisabled = !formData.rightKidney.evaluated ||
                              formData.rightKidney.nephrectomy ||
                              formData.rightKidney.echotexture === 'Not Well Visualized';
  const leftKidneyDisabled = !formData.leftKidney.evaluated ||
                             formData.leftKidney.nephrectomy ||
                             formData.leftKidney.echotexture === 'Not Well Visualized';

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={hookHandleSubmit}>
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button
              type="button"
              className="btn-close"
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history"
            />
          </div>
        </div>

        {/* Aorta */}
        <div className="form-section">
          <h3>Aorta</h3>

          <div className="form-group">
            <label>Diameter (cm):</label>
            <input
              type="number"
              step="0.1"
              placeholder="Measurement"
              value={formData.aorta.diameter}
              onChange={(e) => updateNestedField('aorta', 'diameter', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Peak Systolic Velocity (cm/sec):</label>
            <input
              type="number"
              step="0.1"
              placeholder="Measurement"
              value={formData.aorta.peakSystolicVelocity}
              onChange={(e) => updateNestedField('aorta', 'peakSystolicVelocity', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Waveform:</label>
            <select
              value={formData.aorta.waveform}
              onChange={(e) => updateNestedField('aorta', 'waveform', e.target.value)}
            >
              {WAVEFORM_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            <button
              type="button"
              onClick={() => setActiveTab(0)}
              className={`worksheet-tab-btn${activeTab === 0 ? ' active' : ''}`}
            >
              Right Kidney
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(1)}
              className={`worksheet-tab-btn${activeTab === 1 ? ' active' : ''}`}
            >
              Left Kidney
            </button>
          </div>

          {/* Tab Content */}
          <div className="worksheet-tab-content" style={{ minHeight: '400px' }}>
            {/* Right Kidney Tab */}
            {activeTab === 0 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Right Kidney</h3>

          <label style={{ display: 'block', marginBottom: '15px' }}>
            <input
              type="checkbox"
              checked={formData.rightKidney.evaluated}
              onChange={(e) => handleKidneyChange('rightKidney', 'evaluated', e.target.checked)}
            />
            Evaluated
          </label>

          <div className="form-group">
            <label>Echotexture:</label>
            <select
              value={formData.rightKidney.echotexture}
              onChange={(e) => handleKidneyChange('rightKidney', 'echotexture', e.target.value)}
              disabled={formData.rightKidney.nephrectomy}
            >
              {ECHOTEXTURE_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {!rightKidneyDisabled && (
            <>
              <div className="size-inputs">
                <label>Size (cm):</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Length"
                  value={formData.rightKidney.size1}
                  onChange={(e) => handleKidneyChange('rightKidney', 'size1', e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Width"
                  value={formData.rightKidney.size2}
                  onChange={(e) => handleKidneyChange('rightKidney', 'size2', e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Height"
                  value={formData.rightKidney.size3}
                  onChange={(e) => handleKidneyChange('rightKidney', 'size3', e.target.value)}
                />
                <span className="volume">
                  = {calculateVolume(formData.rightKidney.size1, formData.rightKidney.size2, formData.rightKidney.size3)} cc
                </span>
              </div>

              <div className="form-group">
                <label>Hydronephrosis:</label>
                <select
                  value={formData.rightKidney.hydronephrosis}
                  onChange={(e) => handleKidneyChange('rightKidney', 'hydronephrosis', e.target.value)}
                >
                  {HYDRONEPHROSIS_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="checkbox-group" style={{ marginBottom: '20px' }}>
                <label>Other Findings:</label>
                {KIDNEY_FINDINGS.map(finding => (
                  <label key={finding}>
                    <input
                      type="checkbox"
                      checked={formData.rightKidney.other.includes(finding)}
                      onChange={() => handleKidneyOtherChange('rightKidney', finding)}
                    />
                    {finding}
                  </label>
                ))}
              </div>

              <div className="form-group">
                <label>Peak Systolic Velocity (cm/sec):</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Measurement"
                  value={formData.rightKidney.peakSystolicVelocity}
                  onChange={(e) => handleKidneyChange('rightKidney', 'peakSystolicVelocity', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Waveform:</label>
                <select
                  value={formData.rightKidney.waveform}
                  onChange={(e) => handleKidneyChange('rightKidney', 'waveform', e.target.value)}
                >
                  {WAVEFORM_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="checkbox-group" style={{ marginBottom: '15px' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.rightKidney.aliasing}
                    onChange={(e) => handleKidneyChange('rightKidney', 'aliasing', e.target.checked)}
                  />
                  Aliasing
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.rightKidney.tardusParvus}
                    onChange={(e) => handleKidneyChange('rightKidney', 'tardusParvus', e.target.checked)}
                  />
                  Tardus Parvus
                </label>
              </div>

              <div className="form-group">
                <label>Highest Resistive Index (RI):</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.rightKidney.highestRI}
                  onChange={(e) => handleKidneyChange('rightKidney', 'highestRI', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Lowest Resistive Index (RI):</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.rightKidney.lowestRI}
                  onChange={(e) => handleKidneyChange('rightKidney', 'lowestRI', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Renal-to-Aorta Ratio:</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.rightKidney.renalToAortaRatio}
                  onChange={(e) => handleKidneyChange('rightKidney', 'renalToAortaRatio', e.target.value)}
                  readOnly
                  style={{ backgroundColor: 'var(--color-surface)' }}
                />
              </div>

              <div className="form-group">
                <label>Estimated Stenosis:</label>
                <input
                  type="text"
                  value={formData.rightKidney.estimatedStenosis}
                  readOnly
                  style={{ backgroundColor: 'var(--color-surface)' }}
                  placeholder="Auto-calculated"
                />
              </div>

              <div className="form-group">
                <label>Renal Vein:</label>
                <select
                  value={formData.rightKidney.renalVein}
                  onChange={(e) => handleKidneyChange('rightKidney', 'renalVein', e.target.value)}
                >
                  {RENAL_VEIN_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <button type="button" onClick={() => addKidneyMass('rightKidney')} className="add-mass-btn">
                + Add Right Kidney Mass
              </button>

              {formData.rightKidney.masses.map((mass, index) => (
                <div key={index} className="mass-section">
                  <h4>Mass {index + 1}</h4>
                  <div className="size-inputs">
                    <input
                      type="number"
                      step="0.1"
                      placeholder="L"
                      value={mass.size1}
                      onChange={(e) => handleMassChange('rightKidney', index, 'size1', e.target.value)}
                    />
                    <span>×</span>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="W"
                      value={mass.size2}
                      onChange={(e) => handleMassChange('rightKidney', index, 'size2', e.target.value)}
                    />
                    <span>×</span>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="H"
                      value={mass.size3}
                      onChange={(e) => handleMassChange('rightKidney', index, 'size3', e.target.value)}
                    />
                  </div>
                  <select
                    value={mass.echotexture}
                    onChange={(e) => handleMassChange('rightKidney', index, 'echotexture', e.target.value)}
                  >
                    {MASS_ECHOTEXTURE.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => removeKidneyMass('rightKidney', index)} className="remove-btn">
                    Remove Mass
                  </button>
                </div>
              ))}
            </>
          )}
              </div>
            )}

            {/* Left Kidney Tab */}
            {activeTab === 1 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Left Kidney</h3>

          <label style={{ display: 'block', marginBottom: '15px' }}>
            <input
              type="checkbox"
              checked={formData.leftKidney.evaluated}
              onChange={(e) => handleKidneyChange('leftKidney', 'evaluated', e.target.checked)}
            />
            Evaluated
          </label>

          <div className="form-group">
            <label>Echotexture:</label>
            <select
              value={formData.leftKidney.echotexture}
              onChange={(e) => handleKidneyChange('leftKidney', 'echotexture', e.target.value)}
              disabled={formData.leftKidney.nephrectomy}
            >
              {ECHOTEXTURE_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {!leftKidneyDisabled && (
            <>
              <div className="size-inputs">
                <label>Size (cm):</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Length"
                  value={formData.leftKidney.size1}
                  onChange={(e) => handleKidneyChange('leftKidney', 'size1', e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Width"
                  value={formData.leftKidney.size2}
                  onChange={(e) => handleKidneyChange('leftKidney', 'size2', e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Height"
                  value={formData.leftKidney.size3}
                  onChange={(e) => handleKidneyChange('leftKidney', 'size3', e.target.value)}
                />
                <span className="volume">
                  = {calculateVolume(formData.leftKidney.size1, formData.leftKidney.size2, formData.leftKidney.size3)} cc
                </span>
              </div>

              <div className="form-group">
                <label>Hydronephrosis:</label>
                <select
                  value={formData.leftKidney.hydronephrosis}
                  onChange={(e) => handleKidneyChange('leftKidney', 'hydronephrosis', e.target.value)}
                >
                  {HYDRONEPHROSIS_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="checkbox-group" style={{ marginBottom: '20px' }}>
                <label>Other Findings:</label>
                {KIDNEY_FINDINGS.map(finding => (
                  <label key={finding}>
                    <input
                      type="checkbox"
                      checked={formData.leftKidney.other.includes(finding)}
                      onChange={() => handleKidneyOtherChange('leftKidney', finding)}
                    />
                    {finding}
                  </label>
                ))}
              </div>

              <div className="form-group">
                <label>Peak Systolic Velocity (cm/sec):</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="Measurement"
                  value={formData.leftKidney.peakSystolicVelocity}
                  onChange={(e) => handleKidneyChange('leftKidney', 'peakSystolicVelocity', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Waveform:</label>
                <select
                  value={formData.leftKidney.waveform}
                  onChange={(e) => handleKidneyChange('leftKidney', 'waveform', e.target.value)}
                >
                  {WAVEFORM_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="checkbox-group" style={{ marginBottom: '15px' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.leftKidney.aliasing}
                    onChange={(e) => handleKidneyChange('leftKidney', 'aliasing', e.target.checked)}
                  />
                  Aliasing
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.leftKidney.tardusParvus}
                    onChange={(e) => handleKidneyChange('leftKidney', 'tardusParvus', e.target.checked)}
                  />
                  Tardus Parvus
                </label>
              </div>

              <div className="form-group">
                <label>Highest Resistive Index (RI):</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.leftKidney.highestRI}
                  onChange={(e) => handleKidneyChange('leftKidney', 'highestRI', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Lowest Resistive Index (RI):</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.leftKidney.lowestRI}
                  onChange={(e) => handleKidneyChange('leftKidney', 'lowestRI', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Renal-to-Aorta Ratio:</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.leftKidney.renalToAortaRatio}
                  onChange={(e) => handleKidneyChange('leftKidney', 'renalToAortaRatio', e.target.value)}
                  readOnly
                  style={{ backgroundColor: 'var(--color-surface)' }}
                />
              </div>

              <div className="form-group">
                <label>Estimated Stenosis:</label>
                <input
                  type="text"
                  value={formData.leftKidney.estimatedStenosis}
                  readOnly
                  style={{ backgroundColor: 'var(--color-surface)' }}
                  placeholder="Auto-calculated"
                />
              </div>

              <div className="form-group">
                <label>Renal Vein:</label>
                <select
                  value={formData.leftKidney.renalVein}
                  onChange={(e) => handleKidneyChange('leftKidney', 'renalVein', e.target.value)}
                >
                  {RENAL_VEIN_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <button type="button" onClick={() => addKidneyMass('leftKidney')} className="add-mass-btn">
                + Add Left Kidney Mass
              </button>

              {formData.leftKidney.masses.map((mass, index) => (
                <div key={index} className="mass-section">
                  <h4>Mass {index + 1}</h4>
                  <div className="size-inputs">
                    <input
                      type="number"
                      step="0.1"
                      placeholder="L"
                      value={mass.size1}
                      onChange={(e) => handleMassChange('leftKidney', index, 'size1', e.target.value)}
                    />
                    <span>×</span>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="W"
                      value={mass.size2}
                      onChange={(e) => handleMassChange('leftKidney', index, 'size2', e.target.value)}
                    />
                    <span>×</span>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="H"
                      value={mass.size3}
                      onChange={(e) => handleMassChange('leftKidney', index, 'size3', e.target.value)}
                    />
                  </div>
                  <select
                    value={mass.echotexture}
                    onChange={(e) => handleMassChange('leftKidney', index, 'echotexture', e.target.value)}
                  >
                    {MASS_ECHOTEXTURE.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => removeKidneyMass('leftKidney', index)} className="remove-btn">
                    Remove Mass
                  </button>
                </div>
              ))}
            </>
          )}
              </div>
            )}
          </div>
        </div>

        {/* Bladder */}
        <div className="form-section">
          <h3>Bladder</h3>

          <label>
            <input
              type="checkbox"
              checked={formData.bladder.nwv}
              onChange={(e) => updateNestedField('bladder', 'nwv', e.target.checked)}
            />
            Not Well Visualized
          </label>

          <div className="checkbox-group">
            <label>Findings:</label>
            {BLADDER_FINDINGS.map(finding => (
              <label key={finding}>
                <input
                  type="checkbox"
                  checked={formData.bladder.other.includes(finding)}
                  onChange={() => handleBladderOtherChange(finding)}
                />
                {finding}
              </label>
            ))}
          </div>

          {!formData.bladder.nwv && (
            <>
              <div className="checkbox-group">
                <label>Jets:</label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.bladder.rightJet}
                    onChange={(e) => updateNestedField('bladder', 'rightJet', e.target.checked)}
                  />
                  Right Jet
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.bladder.leftJet}
                    onChange={(e) => updateNestedField('bladder', 'leftJet', e.target.checked)}
                  />
                  Left Jet
                </label>
              </div>

              <div className="form-group">
                <label>Pre-void (cc):</label>
                <input
                  type="number"
                  value={formData.bladder.prevoid}
                  onChange={(e) => updateNestedField('bladder', 'prevoid', e.target.value)}
                />
              </div>

              <label>
                <input
                  type="checkbox"
                  checked={formData.bladder.noVoid}
                  onChange={(e) => updateNestedField('bladder', 'noVoid', e.target.checked)}
                />
                Patient Unable To Void
              </label>

              {!formData.bladder.noVoid && (
                <div className="form-group">
                  <label>Post-void (PVR) (cc):</label>
                  <input
                    type="number"
                    value={formData.bladder.postvoid}
                    onChange={(e) => updateNestedField('bladder', 'postvoid', e.target.value)}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="submit-btn"
          >
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button
            type="button"
            onClick={resetForm}
            disabled={isLoading}
            className="reset-btn"
          >
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default RenalArteryDopplerForm;
